import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Typography,
  Button,
  TextField,
  Tooltip,
  Box,
  FormControlLabel,
  Switch,
  Divider,
  MenuItem,
  Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useGlobalSettingState, useComponentWebSocket } from './GlobalSettingUtils';
import ExtraTokenUsageChip from '../Common/ExtraTokenUsageChip';
import ProjectHistoryRetentionSettings from './ProjectHistoryRetentionSettings';

const DEFAULT_POSTPROCESSING = {
  action_timeout_seconds: 1800,
  subprocess_loop_timeout_seconds: 1800,
  pdf_render_node_path: ''
};

const DEFAULT_MASTER_IMPORT_MANAGER = {
  file_process_timeout_seconds: 600,
  max_time_to_process_batch: 3600,
  max_files_in_batch: 500,
  max_semaphore: 16,
  max_workers: 8,
  worker_status_log_interval_seconds: 30,
  worker_batch_check_interval_seconds: 2,
  worker_idle_sleep_seconds: 0.1,
  batch_completion_grace_seconds: 2,
  continuous_batch_pause_seconds: 5,
  dynamic_max_wait_enabled: true,
  min_time_to_process_batch: 60,
  per_queued_file_delay_seconds: 0.5,
  stalled_queue_timeout_seconds: 60,
  no_new_activity_timeout_seconds: 30,
  queue_folder_batch_size: 1000,
  queue_folder_scan_concurrency: 8,
  queue_folder_shutdown_threshold_minutes: 30,
  status_broadcast_min_interval_seconds: 2
};

const DEFAULT_SSL_SERVICES = {
  canManage: false,
  localOnly: true,
  webserver: {
    host: 'localhost',
    scheme: 'http',
    port: 5000,
    certfile: '',
    keyfile: '',
    skip_tls_verify: false
  },
  websocket: {
    host: 'localhost',
    scheme: 'http',
    port: 6789,
    certfile: '',
    keyfile: '',
    http_host: 'localhost',
    http_scheme: 'http',
    http_port: 6792,
    http_certfile: '',
    http_keyfile: ''
  }
};

const toNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const listToText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean).join(', ');
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
};

const textToList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value !== 'string') {
    return [];
  }
  return value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const mergeSection = (incoming, defaults) => {
  const source = incoming && typeof incoming === 'object' ? incoming : {};
  return {
    ...defaults,
    ...source
  };
};

const SystemSettings = () => {
  const {
    isModified,
    setIsModified,
    t,
    openSnackbar
  } = useGlobalSettingState();

  const [systemConfig, setSystemConfig] = useState({
    allowUserPluginGeneration: true,
    paymentsEnabled: true,
    showWidgetEvidence: true,
    restrictToUserFolder: false,
    searchThreshold: 1.4,
    aiVisionMaxRetries: 5,
    maxAudioRecordingLengthSeconds: 7200,
    allowedConnectorsText: '',
    disabledConnectorsText: '',
    postprocessing: DEFAULT_POSTPROCESSING,
    master_import_manager: DEFAULT_MASTER_IMPORT_MANAGER,
    sslServices: DEFAULT_SSL_SERVICES,
    widgetPlanningStrategy: 'normal'
  });

  const applyConfigData = useCallback((data) => {
    if (!data || typeof data !== 'object') {
      return;
    }

    setSystemConfig((prev) => ({
      ...prev,
      allowUserPluginGeneration: data.allowUserPluginGeneration ?? prev.allowUserPluginGeneration,
      paymentsEnabled: data.paymentsEnabled ?? prev.paymentsEnabled,
      showWidgetEvidence: data.showWidgetEvidence ?? prev.showWidgetEvidence,
      restrictToUserFolder: data.restrictToUserFolder ?? prev.restrictToUserFolder,
      searchThreshold: data.searchThreshold ?? prev.searchThreshold,
      aiVisionMaxRetries: data.aiVisionMaxRetries ?? prev.aiVisionMaxRetries,
      widgetPlanningStrategy: data.widgetPlanningStrategy ?? prev.widgetPlanningStrategy,
      maxAudioRecordingLengthSeconds: data.max_audio_recording_length_seconds ?? data.maxAudioRecordingLengthSeconds,
      allowedConnectorsText: data.allowedConnectors !== undefined ? listToText(data.allowedConnectors) : prev.allowedConnectorsText,
      disabledConnectorsText: data.disabledConnectors !== undefined ? listToText(data.disabledConnectors) : prev.disabledConnectorsText,
      postprocessing: mergeSection(data.postprocessing, prev.postprocessing),
      master_import_manager: mergeSection(data.master_import_manager, prev.master_import_manager),
      sslServices: data.sslServices
        ? {
            ...prev.sslServices,
            ...data.sslServices,
            webserver: mergeSection(data.sslServices.webserver, prev.sslServices.webserver),
            websocket: mergeSection(data.sslServices.websocket, prev.sslServices.websocket)
          }
        : prev.sslServices
    }));
  }, []);

  const commandHandlers = {
    update_configuration: (message) => {
      if (message.data === 'success') {
        openSnackbar(t('global_setting.save_completed'), 3000);
        setIsModified(false);
      } else if (message.data?.error) {
        openSnackbar(message.data.error, 5000);
      }
    },
    get_performance_settings: (message) => {
      applyConfigData(message.data);
    },
    configuration_data: (message) => {
      applyConfigData(message.data);
    }
  };

  const { sendCommand } = useComponentWebSocket(commandHandlers);

  useEffect(() => {
    sendCommand('configuration_check');
  }, [sendCommand]);

  const updateField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSystemConfig((prev) => ({
      ...prev,
      [field]: value
    }));
    setIsModified(true);
  };

  const updateSectionField = (section, field, numeric = false) => (event) => {
    const value = numeric ? event.target.value : event.target.value;
    setSystemConfig((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [field]: value
      }
    }));
    setIsModified(true);
  };

  const updateNestedSectionField = (section, nestedSection, field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSystemConfig((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [nestedSection]: {
          ...((prev[section] || {})[nestedSection] || {}),
          [field]: value
        }
      }
    }));
    setIsModified(true);
  };

  const handleToggle = (field) => (event) => {
    setSystemConfig((prev) => ({
      ...prev,
      [field]: event.target.checked
    }));
    setIsModified(true);
  };

  const renderToggleWithHelp = (field, labelKey, labelFallback, helpKey, helpFallback) => (
    <FormControlLabel
      control={<Switch checked={Boolean(systemConfig[field])} onChange={handleToggle(field)} />}
      label={
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Typography component="span" variant="body2">
            {t(labelKey, labelFallback)}
          </Typography>
          <Tooltip title={t(helpKey, helpFallback)} placement="top">
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
      }
    />
  );

  const handleSave = () => {
    if (!isModified) {
      return;
    }

    const payload = {
      allowUserPluginGeneration: systemConfig.allowUserPluginGeneration,
      paymentsEnabled: systemConfig.paymentsEnabled,
      showWidgetEvidence: systemConfig.showWidgetEvidence,
      widgetPlanningStrategy: systemConfig.widgetPlanningStrategy,
      restrictToUserFolder: systemConfig.restrictToUserFolder,
      searchThreshold: toNumber(systemConfig.searchThreshold, 1.4),
      aiVisionMaxRetries: toNumber(systemConfig.aiVisionMaxRetries, 5),
      max_audio_recording_length_seconds: toNumber(systemConfig.maxAudioRecordingLengthSeconds, 7200),
      allowedConnectors: textToList(systemConfig.allowedConnectorsText),
      disabledConnectors: textToList(systemConfig.disabledConnectorsText),
      postprocessing: {
        action_timeout_seconds: toNumber(systemConfig.postprocessing.action_timeout_seconds, DEFAULT_POSTPROCESSING.action_timeout_seconds),
        subprocess_loop_timeout_seconds: toNumber(systemConfig.postprocessing.subprocess_loop_timeout_seconds, DEFAULT_POSTPROCESSING.subprocess_loop_timeout_seconds),
        pdf_render_node_path: String(systemConfig.postprocessing.pdf_render_node_path || '').trim()
      },
      master_import_manager: {
        file_process_timeout_seconds: toNumber(systemConfig.master_import_manager.file_process_timeout_seconds, DEFAULT_MASTER_IMPORT_MANAGER.file_process_timeout_seconds),
        max_time_to_process_batch: toNumber(systemConfig.master_import_manager.max_time_to_process_batch, DEFAULT_MASTER_IMPORT_MANAGER.max_time_to_process_batch),
        max_files_in_batch: toNumber(systemConfig.master_import_manager.max_files_in_batch, DEFAULT_MASTER_IMPORT_MANAGER.max_files_in_batch),
        max_semaphore: toNumber(systemConfig.master_import_manager.max_semaphore, DEFAULT_MASTER_IMPORT_MANAGER.max_semaphore),
        max_workers: toNumber(systemConfig.master_import_manager.max_workers, DEFAULT_MASTER_IMPORT_MANAGER.max_workers),
        worker_status_log_interval_seconds: toNumber(systemConfig.master_import_manager.worker_status_log_interval_seconds, DEFAULT_MASTER_IMPORT_MANAGER.worker_status_log_interval_seconds),
        worker_batch_check_interval_seconds: toNumber(systemConfig.master_import_manager.worker_batch_check_interval_seconds, DEFAULT_MASTER_IMPORT_MANAGER.worker_batch_check_interval_seconds),
        worker_idle_sleep_seconds: toNumber(systemConfig.master_import_manager.worker_idle_sleep_seconds, DEFAULT_MASTER_IMPORT_MANAGER.worker_idle_sleep_seconds),
        batch_completion_grace_seconds: toNumber(systemConfig.master_import_manager.batch_completion_grace_seconds, DEFAULT_MASTER_IMPORT_MANAGER.batch_completion_grace_seconds),
        continuous_batch_pause_seconds: toNumber(systemConfig.master_import_manager.continuous_batch_pause_seconds, DEFAULT_MASTER_IMPORT_MANAGER.continuous_batch_pause_seconds),
        dynamic_max_wait_enabled: Boolean(systemConfig.master_import_manager.dynamic_max_wait_enabled),
        min_time_to_process_batch: toNumber(systemConfig.master_import_manager.min_time_to_process_batch, DEFAULT_MASTER_IMPORT_MANAGER.min_time_to_process_batch),
        per_queued_file_delay_seconds: toNumber(systemConfig.master_import_manager.per_queued_file_delay_seconds, DEFAULT_MASTER_IMPORT_MANAGER.per_queued_file_delay_seconds),
        stalled_queue_timeout_seconds: toNumber(systemConfig.master_import_manager.stalled_queue_timeout_seconds, DEFAULT_MASTER_IMPORT_MANAGER.stalled_queue_timeout_seconds),
        no_new_activity_timeout_seconds: toNumber(systemConfig.master_import_manager.no_new_activity_timeout_seconds, DEFAULT_MASTER_IMPORT_MANAGER.no_new_activity_timeout_seconds),
        queue_folder_batch_size: toNumber(systemConfig.master_import_manager.queue_folder_batch_size, DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_batch_size),
        queue_folder_scan_concurrency: toNumber(systemConfig.master_import_manager.queue_folder_scan_concurrency, DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_scan_concurrency),
        queue_folder_shutdown_threshold_minutes: toNumber(systemConfig.master_import_manager.queue_folder_shutdown_threshold_minutes, DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_shutdown_threshold_minutes),
        status_broadcast_min_interval_seconds: toNumber(systemConfig.master_import_manager.status_broadcast_min_interval_seconds, DEFAULT_MASTER_IMPORT_MANAGER.status_broadcast_min_interval_seconds)
      }
    };

    if (systemConfig.sslServices?.canManage) {
      payload.sslServices = {
        webserver: {
          host: String(systemConfig.sslServices.webserver.host || '').trim(),
          scheme: String(systemConfig.sslServices.webserver.scheme || 'http').trim(),
          port: toNumber(systemConfig.sslServices.webserver.port, DEFAULT_SSL_SERVICES.webserver.port),
          certfile: String(systemConfig.sslServices.webserver.certfile || '').trim(),
          keyfile: String(systemConfig.sslServices.webserver.keyfile || '').trim(),
          skip_tls_verify: Boolean(systemConfig.sslServices.webserver.skip_tls_verify)
        },
        websocket: {
          host: String(systemConfig.sslServices.websocket.host || '').trim(),
          scheme: String(systemConfig.sslServices.websocket.scheme || 'http').trim(),
          port: toNumber(systemConfig.sslServices.websocket.port, DEFAULT_SSL_SERVICES.websocket.port),
          certfile: String(systemConfig.sslServices.websocket.certfile || '').trim(),
          keyfile: String(systemConfig.sslServices.websocket.keyfile || '').trim(),
          http_host: String(systemConfig.sslServices.websocket.http_host || '').trim(),
          http_scheme: String(systemConfig.sslServices.websocket.http_scheme || 'http').trim(),
          http_port: toNumber(systemConfig.sslServices.websocket.http_port, DEFAULT_SSL_SERVICES.websocket.http_port),
          http_certfile: String(systemConfig.sslServices.websocket.http_certfile || '').trim(),
          http_keyfile: String(systemConfig.sslServices.websocket.http_keyfile || '').trim()
        }
      };
    }

    sendCommand('update_configuration', payload)
      .then(() => sendCommand('configuration_check'))
      .catch((error) => {
        openSnackbar(
          error?.message || t('global_setting.save_failed', 'Save failed'),
          5000
        );
      });
  };

  const renderTokenAwareLabel = (label, labelKey = 'settings.others.affects_token_usage', fallback = 'Affects token usage') => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <span>{label}</span>
      <ExtraTokenUsageChip
        labelKey={labelKey}
        fallback={fallback}
        sx={{ mb: 0, height: 18, fontSize: '0.6rem' }}
      />
    </Box>
  );

  const renderNumberField = (
    section,
    field,
    label,
    helperText,
    fallback,
    min = 0,
    step = 1,
    tokenUsageLabelKey = '',
    tokenUsageFallback = 'Affects token usage'
  ) => (
    <TextField
      label={tokenUsageLabelKey
        ? renderTokenAwareLabel(t(`global_setting.${field}`, label), tokenUsageLabelKey, tokenUsageFallback)
        : t(`global_setting.${field}`, label)}
      value={systemConfig[section] ? systemConfig[section][field] : systemConfig[field] ?? fallback}
      onChange={section ? updateSectionField(section, field, true) : updateField(field)}
      fullWidth
      type="number"
      inputProps={{ min, step }}
      helperText={helperText}
      sx={{ mt: 1.5 }}
    />
  );

  const renderSslTextField = (service, field, label, helperText = '') => (
    <TextField
      label={label}
      value={systemConfig.sslServices?.[service]?.[field] ?? ''}
      onChange={updateNestedSectionField('sslServices', service, field)}
      fullWidth
      helperText={helperText}
      sx={{ mt: 1.5 }}
    />
  );

  const renderSslNumberField = (service, field, label, fallback) => (
    <TextField
      label={label}
      value={systemConfig.sslServices?.[service]?.[field] ?? fallback}
      onChange={updateNestedSectionField('sslServices', service, field)}
      fullWidth
      type="number"
      inputProps={{ min: 1, max: 65535, step: 1 }}
      sx={{ mt: 1.5 }}
    />
  );

  const renderSslSchemeField = (service, field, label, options) => (
    <TextField
      select
      label={label}
      value={systemConfig.sslServices?.[service]?.[field] ?? options[0]}
      onChange={updateNestedSectionField('sslServices', service, field)}
      fullWidth
      sx={{ mt: 1.5 }}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" gutterBottom>
            {t('global_setting.system_section', 'System & Runtime')}
          </Typography>
          <Tooltip
            title={t('global_setting.system_section_tip', 'Expose operational settings that affect ingestion and post-processing. Secrets and path-sensitive storage settings remain in config.toml only.')}
            placement="top"
          >
            <InfoOutlinedIcon fontSize="small" sx={{ cursor: 'pointer' }} />
          </Tooltip>
        </Box>
      </Grid>

      <Grid item xs={12}><ProjectHistoryRetentionSettings /></Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('global_setting.system_access_title', 'Access and feature toggles')}
        </Typography>
        <TextField
          select
          fullWidth
          label={t('global_setting.widget_planning_strategy')}
          value={systemConfig.widgetPlanningStrategy}
          onChange={updateField('widgetPlanningStrategy')}
          helperText={t('global_setting.widget_planning_strategy_help')}
          sx={{ mb: 2 }}
        >
          <MenuItem value="normal">{t('global_setting.widget_planning_normal')}</MenuItem>
          <MenuItem value="agentic">{t('global_setting.widget_planning_agentic')}</MenuItem>
        </TextField>
        {renderToggleWithHelp(
          'allowUserPluginGeneration',
          'global_setting.allow_user_plugin_generation',
          'Allow users to generate plugins',
          'global_setting.allow_user_plugin_generation_help',
          'Lets non-admin users create plugins from the UI.'
        )}
        {renderToggleWithHelp(
          'paymentsEnabled',
          'global_setting.payments_enabled',
          'Enable payments',
          'global_setting.payments_enabled_help',
          'Controls whether credit purchases are shown and accepted.'
        )}
        {renderToggleWithHelp(
          'showWidgetEvidence',
          'global_setting.show_widget_evidence',
          'Show widget evidence',
          'global_setting.show_widget_evidence_help',
          'Shows the evidence dialogs and related widget proof details.'
        )}
        {renderToggleWithHelp(
          'restrictToUserFolder',
          'global_setting.restrict_to_user_folder',
          'Restrict to user folder',
          'global_setting.restrict_to_user_folder_help',
          'Limits file browsing and downloads to the user folder.'
        )}
        <TextField
          label={t('global_setting.allowed_connectors', 'Allowed connectors')}
          value={systemConfig.allowedConnectorsText}
          onChange={updateField('allowedConnectorsText')}
          fullWidth
          multiline
          minRows={2}
          helperText={t('global_setting.allowed_connectors_helper', 'Comma-separated connector names. Leave empty to allow all connectors.')}
          sx={{ mt: 2 }}
        />
        <TextField
          label={t('global_setting.disabled_connectors', 'Disabled connectors')}
          value={systemConfig.disabledConnectorsText}
          onChange={updateField('disabledConnectorsText')}
          fullWidth
          multiline
          minRows={2}
          helperText={t('global_setting.disabled_connectors_helper', 'Comma-separated connector names that must stay disabled.')}
          sx={{ mt: 2 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('global_setting.runtime_limits_title', 'AI and runtime limits')}
        </Typography>
        {renderNumberField(null, 'searchThreshold', 'Search threshold', t('global_setting.search_threshold_helper', 'Higher values make search stricter.'), 1.4, 0, 0.1)}
        {renderNumberField(
          null,
          'aiVisionMaxRetries',
          'Vision retries',
          t('global_setting.ai_vision_max_retries_helper', 'Maximum retry count for vision calls.'),
          5,
          0,
          1,
          'settings.others.may_use_extra_tokens',
          'May use extra tokens'
        )}
        {renderNumberField(null, 'maxAudioRecordingLengthSeconds', 'Max audio recording length', t('global_setting.max_audio_recording_length_seconds_helper', 'Maximum client-side audio duration in seconds.'), 7200, 1, 1)}
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
      </Grid>

      {systemConfig.sslServices?.canManage && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {t('global_setting.ssl_services_title', 'SSL service configuration')}
            </Typography>
            <Alert severity="info" sx={{ mb: 1 }}>
              {t('global_setting.ssl_services_restart_notice', 'Saved SSL changes are written to the service config files. Restart DataUnfold services for listener changes to take effect.')}
            </Alert>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('global_setting.ssl_webserver_title', 'Webserver HTTPS')}
            </Typography>
            {renderSslTextField('webserver', 'host', t('global_setting.ssl_host', 'Host'))}
            {renderSslSchemeField('webserver', 'scheme', t('global_setting.ssl_scheme', 'Scheme'), ['http', 'https'])}
            {renderSslNumberField('webserver', 'port', t('global_setting.ssl_port', 'Port'), DEFAULT_SSL_SERVICES.webserver.port)}
            {renderSslTextField('webserver', 'certfile', t('global_setting.ssl_certfile', 'Certificate file'), t('global_setting.ssl_certfile_helper', 'Absolute path to the PEM certificate file.'))}
            {renderSslTextField('webserver', 'keyfile', t('global_setting.ssl_keyfile', 'Private key file'), t('global_setting.ssl_keyfile_helper', 'Absolute path to the PEM private key file.'))}
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={Boolean(systemConfig.sslServices.webserver.skip_tls_verify)}
                  onChange={updateNestedSectionField('sslServices', 'webserver', 'skip_tls_verify')}
                />
              }
              label={t('global_setting.ssl_skip_tls_verify', 'Skip TLS verification for local health checks')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('global_setting.ssl_websocket_title', 'WebSocket WSS')}
            </Typography>
            {renderSslTextField('websocket', 'host', t('global_setting.ssl_host', 'Host'))}
            {renderSslSchemeField('websocket', 'scheme', t('global_setting.ssl_scheme', 'Scheme'), ['http', 'https', 'ws', 'wss'])}
            {renderSslNumberField('websocket', 'port', t('global_setting.ssl_port', 'Port'), DEFAULT_SSL_SERVICES.websocket.port)}
            {renderSslTextField('websocket', 'certfile', t('global_setting.ssl_certfile', 'Certificate file'), t('global_setting.ssl_certfile_helper', 'Absolute path to the PEM certificate file.'))}
            {renderSslTextField('websocket', 'keyfile', t('global_setting.ssl_keyfile', 'Private key file'), t('global_setting.ssl_keyfile_helper', 'Absolute path to the PEM private key file.'))}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('global_setting.ssl_websocket_api_title', 'WebSocket internal HTTP API')}
            </Typography>
            {renderSslTextField('websocket', 'http_host', t('global_setting.ssl_http_host', 'HTTP API host'))}
            {renderSslSchemeField('websocket', 'http_scheme', t('global_setting.ssl_http_scheme', 'HTTP API scheme'), ['http', 'https'])}
            {renderSslNumberField('websocket', 'http_port', t('global_setting.ssl_http_port', 'HTTP API port'), DEFAULT_SSL_SERVICES.websocket.http_port)}
            {renderSslTextField('websocket', 'http_certfile', t('global_setting.ssl_http_certfile', 'HTTP API certificate file'))}
            {renderSslTextField('websocket', 'http_keyfile', t('global_setting.ssl_http_keyfile', 'HTTP API private key file'))}
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>
        </>
      )}

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('global_setting.postprocessing_section', 'Post-processing runtime')}
        </Typography>
        {renderNumberField('postprocessing', 'action_timeout_seconds', 'Action timeout (seconds)', t('global_setting.action_timeout_seconds_helper', 'Timeout for one built-in post-processing action.'), DEFAULT_POSTPROCESSING.action_timeout_seconds)}
        {renderNumberField('postprocessing', 'subprocess_loop_timeout_seconds', 'Subprocess loop timeout (seconds)', t('global_setting.subprocess_loop_timeout_seconds_helper', 'Timeout for the internal subprocess-compatible event loop.'), DEFAULT_POSTPROCESSING.subprocess_loop_timeout_seconds)}
        <TextField
          label={t('global_setting.pdf_render_node_path', 'PDF render Node.js path')}
          value={systemConfig.postprocessing.pdf_render_node_path}
          onChange={updateSectionField('postprocessing', 'pdf_render_node_path')}
          fullWidth
          helperText={t('global_setting.pdf_render_node_path_helper', 'Optional Node executable for PDF rendering. Leave empty to use PATH.')}
          sx={{ mt: 2 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('global_setting.master_import_section', 'Ingestion limits')}
        </Typography>
        {renderNumberField('master_import_manager', 'file_process_timeout_seconds', 'File process timeout (seconds)', t('global_setting.file_process_timeout_seconds_helper', 'Per-file timeout used by the import manager.'), DEFAULT_MASTER_IMPORT_MANAGER.file_process_timeout_seconds)}
        {renderNumberField('master_import_manager', 'max_time_to_process_batch', 'Max batch process time', t('global_setting.max_time_to_process_batch_helper', 'Maximum processing window for a batch of files.'), DEFAULT_MASTER_IMPORT_MANAGER.max_time_to_process_batch)}
        {renderNumberField('master_import_manager', 'max_files_in_batch', 'Max files in batch', t('global_setting.max_files_in_batch_helper', 'Upper limit for one ingestion batch.'), DEFAULT_MASTER_IMPORT_MANAGER.max_files_in_batch)}
        {renderNumberField('master_import_manager', 'max_workers', 'Max workers', t('global_setting.max_workers_helper', 'Worker pool size for ingestion processing.'), DEFAULT_MASTER_IMPORT_MANAGER.max_workers)}
        {renderNumberField('master_import_manager', 'max_semaphore', 'Max semaphore', t('global_setting.max_semaphore_helper', 'Semaphore limit for ingestion concurrency.'), DEFAULT_MASTER_IMPORT_MANAGER.max_semaphore)}
        {renderNumberField('master_import_manager', 'worker_status_log_interval_seconds', 'Worker status log interval', t('global_setting.worker_status_log_interval_seconds_helper', 'How often worker status is logged.'), DEFAULT_MASTER_IMPORT_MANAGER.worker_status_log_interval_seconds)}
        {renderNumberField('master_import_manager', 'worker_batch_check_interval_seconds', 'Worker batch check interval', t('global_setting.worker_batch_check_interval_seconds_helper', 'Interval between batch checks.'), DEFAULT_MASTER_IMPORT_MANAGER.worker_batch_check_interval_seconds)}
        {renderNumberField('master_import_manager', 'worker_idle_sleep_seconds', 'Worker idle sleep', t('global_setting.worker_idle_sleep_seconds_helper', 'Sleep duration when the worker is idle.'), DEFAULT_MASTER_IMPORT_MANAGER.worker_idle_sleep_seconds, 0, 0.1)}
        {renderNumberField('master_import_manager', 'batch_completion_grace_seconds', 'Batch completion grace', t('global_setting.batch_completion_grace_seconds_helper', 'Grace window before batch completion is finalized.'), DEFAULT_MASTER_IMPORT_MANAGER.batch_completion_grace_seconds, 0, 0.1)}
        {renderNumberField('master_import_manager', 'continuous_batch_pause_seconds', 'Continuous batch pause', t('global_setting.continuous_batch_pause_seconds_helper', 'Pause between continuous batch runs.'), DEFAULT_MASTER_IMPORT_MANAGER.continuous_batch_pause_seconds, 0, 0.1)}
        {renderNumberField('master_import_manager', 'queue_folder_batch_size', 'Queue folder batch size', t('global_setting.queue_folder_batch_size_helper', 'Maximum files scanned per queue-folder batch.'), DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_batch_size)}
        {renderNumberField('master_import_manager', 'queue_folder_scan_concurrency', 'Queue folder scan concurrency', t('global_setting.queue_folder_scan_concurrency_helper', 'Number of concurrent queue-folder scanners.'), DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_scan_concurrency)}
        {renderNumberField('master_import_manager', 'queue_folder_shutdown_threshold_minutes', 'Queue folder shutdown threshold', t('global_setting.queue_folder_shutdown_threshold_minutes_helper', 'How long a queue folder may stay stale before shutdown.'), DEFAULT_MASTER_IMPORT_MANAGER.queue_folder_shutdown_threshold_minutes, 0, 1)}
        {renderNumberField('master_import_manager', 'status_broadcast_min_interval_seconds', 'Status broadcast interval', t('global_setting.status_broadcast_min_interval_seconds_helper', 'Minimum interval between status broadcasts.'), DEFAULT_MASTER_IMPORT_MANAGER.status_broadcast_min_interval_seconds, 0, 0.1)}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {t('global_setting.batch_trigger_policy_title', 'Batch trigger policy')}
        </Typography>
        {renderToggleWithHelp(
          'dynamic_max_wait_enabled',
          'global_setting.dynamic_max_wait_enabled',
          'Enable dynamic batch wait',
          'global_setting.dynamic_max_wait_enabled_help',
          'Lets the ingestion worker decide when a batch is ready using queued file counts, activity timing, and safety guards.'
        )}
        {renderNumberField('master_import_manager', 'min_time_to_process_batch', 'Minimum batch wait (seconds)', t('global_setting.min_time_to_process_batch_helper', 'Base wait before a batch can be considered ready.'), DEFAULT_MASTER_IMPORT_MANAGER.min_time_to_process_batch, 0, 0.1)}
        {renderNumberField('master_import_manager', 'per_queued_file_delay_seconds', 'Per queued file delay (seconds)', t('global_setting.per_queued_file_delay_seconds_helper', 'Extra wait added per queued file when dynamic batch waiting is enabled.'), DEFAULT_MASTER_IMPORT_MANAGER.per_queued_file_delay_seconds, 0, 0.1)}
        {renderNumberField('master_import_manager', 'stalled_queue_timeout_seconds', 'Stalled queue timeout (seconds)', t('global_setting.stalled_queue_timeout_seconds_helper', 'Triggers batch processing when files are queued but nothing has started for too long.'), DEFAULT_MASTER_IMPORT_MANAGER.stalled_queue_timeout_seconds, 0, 1)}
        {renderNumberField('master_import_manager', 'no_new_activity_timeout_seconds', 'No new activity timeout (seconds)', t('global_setting.no_new_activity_timeout_seconds_helper', 'Triggers batch processing earlier when file arrivals stop and the queue becomes quiet.'), DEFAULT_MASTER_IMPORT_MANAGER.no_new_activity_timeout_seconds, 0, 1)}
      </Grid>

      <Grid item xs={12} sx={{ mt: 2, textAlign: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!isModified}
          sx={{
            backgroundColor: '#5757b0',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#aaaaca',
            },
            py: 1,
            px: 3,
            borderRadius: 2,
          }}
        >
          {t('global_setting.save_button') || 'Save System Settings'}
        </Button>
      </Grid>
    </Grid>
  );
};

export { SystemSettings };
